/** The cameras and microphones a person can record with, and the pair they chose last time on this device. */

export interface Device {
  id: string;
  label: string;
}

export interface DeviceLists {
  cameras: Device[];
  mics: Device[];
}

/** The ids chosen on this device. Absent means the browser's own pick. */
export interface DeviceChoice {
  camera?: string;
  mic?: string;
}

const KEY = "flashroom.recorder.devices";

export function loadChoice(): DeviceChoice {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "{}") as Record<string, unknown>;
    return {
      ...(typeof raw.camera === "string" ? { camera: raw.camera } : {}),
      ...(typeof raw.mic === "string" ? { mic: raw.mic } : {}),
    };
  } catch {
    return {};
  }
}

export function saveChoice(choice: DeviceChoice): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(choice));
  } catch {
    // Why nothing: a private window or blocked storage only means the choice is not remembered.
  }
}

/** Only usable devices, named: "communications" repeats the default one, and a browser may hold a name back. */
export function toDevices(all: Array<Pick<MediaDeviceInfo, "kind" | "deviceId" | "label">>): DeviceLists {
  const named = (kind: MediaDeviceKind, word: string): Device[] =>
    all
      .filter((d) => d.kind === kind && d.deviceId && d.deviceId !== "communications")
      .map((d, i) => ({ id: d.deviceId, label: d.label || `${word} ${i + 1}` }));
  return { cameras: named("videoinput", "Camera"), mics: named("audioinput", "Microphone") };
}

export async function allDevices(): Promise<MediaDeviceInfo[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  try {
    return await navigator.mediaDevices.enumerateDevices();
  } catch {
    return [];
  }
}

/** Which listed device a stream is using. A browser's default may carry another id than its listed twin, so the group matches too. */
export function inUse(list: Device[], all: Array<Pick<MediaDeviceInfo, "deviceId" | "groupId">>, track: MediaStreamTrack | undefined): string | undefined {
  const s = track?.getSettings();
  if (!s) return undefined;
  if (s.deviceId && list.some((d) => d.id === s.deviceId)) return s.deviceId;
  const twin = all.find((d) => d.groupId && d.groupId === s.groupId && list.some((l) => l.id === d.deviceId));
  return twin?.deviceId ?? list[0]?.id;
}

/**
 * What the camera is asked for: full HD at 30 frames. Why ideals, never exact or min: a camera that cannot do 1080p
 * gives its best size nearer to it instead of failing, and a phone held upright gives an upright picture. Without a
 * size the browser falls back to 640 x 480. The live room asks the same in `live/meeting/liveMedia.ts`.
 */
const CAMERA_SIZE = { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } } as const;

/** Why this much: sharp at 1080p, and a ten minute take stays near 375 MB, which the note upload carries in parts. */
export const VIDEO_BITS_PER_SECOND = 5_000_000;

/** Why exact, never a wish: Chrome ignores an ideal microphone and gives the default, so a pick would not stick. */
export function streamRequest(choice: DeviceChoice): MediaStreamConstraints {
  return {
    video: { ...(choice.camera ? { deviceId: { exact: choice.camera } } : { facingMode: "user" }), ...CAMERA_SIZE },
    audio: choice.mic ? { deviceId: { exact: choice.mic } } : true,
  };
}

/** The recording kind this browser makes: an MP4 where it can, else WebM. */
export function recordingType(isSupported: (t: string) => boolean): string {
  for (const t of ["video/mp4;codecs=avc1,mp4a", "video/mp4", "video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"]) {
    if (isSupported(t)) return t;
  }
  return "";
}
